import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { usePantryName } from './usePantryName';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Paprika, PaprikaTypography, PaprikaFreshness } from '../../styles/theme';
import { PantryItemView } from '../../redux/pantrySelectors';
import { daysUntilExpiry } from '../../services/pantryFreshnessService';
import { categoryIcon } from './categoryMeta';

interface ReviewFlowProps {
  list: PantryItemView[];
  onKeep: (itemId: string) => void;
  onToss: (itemId: string) => void;
  onDone: (reviewedCount: number) => void;
}

export function ReviewFlow({ list, onKeep, onToss, onDone }: ReviewFlowProps) {
  const { t } = useTranslation();
  const pantryName = usePantryName();

  // Capture initial list of IDs once at mount to handle list shrinking as items are resolved
  // useRef initializer runs once; never recomputes even as snapshot list shrinks
  const initialIds = useRef(list.map((v) => v.item.id)).current;

  // Position walks initialIds (the captured order), NOT the live list —
  // answered items leave the live snapshot, so live indexes go stale.
  const [position, setPosition] = useState(0);
  const doneFired = useRef(false);

  // Current = first captured id from `position` that still exists in the live list
  const current = useMemo(() => {
    for (let i = position; i < initialIds.length; i++) {
      const view = list.find((v) => v.item.id === initialIds[i]);
      if (view) return { view, idx: i };
    }
    return null;
  }, [position, list, initialIds]);

  // When every captured item is resolved, finish exactly once
  useEffect(() => {
    if (!current && !doneFired.current) {
      doneFired.current = true;
      onDone(initialIds.length);
    }
  }, [current, onDone, initialIds.length]);

  if (!current) {
    return null;
  }

  const currentView = current.view;
  const currentIndex = current.idx;
  const { item } = currentView;
  const daysLeft = daysUntilExpiry({ expiryDate: currentView.item.expiryDate });
  const isExpired = daysLeft !== null && daysLeft < 0;
  const isDateless = daysLeft === null;

  // For dateless items, compute age from dateAdded
  let ageDays = 0;
  if (isDateless && item.dateAdded) {
    const added = new Date(item.dateAdded);
    const today = new Date();
    ageDays = Math.floor((today.getTime() - added.getTime()) / (1000 * 60 * 60 * 24));
  }

  const question = isExpired ? t('pantry.review.stillGoodQ') : t('pantry.review.stillThere');
  const note = isExpired
    ? t('pantry.review.expiredNote')
    : t('pantry.review.addedAgo', { count: ageDays });

  const keepButtonLabel = isExpired
    ? t('pantry.review.yesPlusDays')
    : t('pantry.review.yesStillThere');

  const handleKeep = () => {
    onKeep(item.id);
    setPosition(currentIndex + 1);
  };

  const handleToss = () => {
    onToss(item.id);
    setPosition(currentIndex + 1);
  };

  const totalItems = initialIds.length;
  const currentNum = currentIndex + 1;

  // Get category icon
  const iconRef = categoryIcon(item.category, item.name);

  return (
    <View style={styles.container}>
      {/* Progress dots */}
      <View style={styles.dotsContainer}>
        {initialIds.map((_, idx) => (
          <View
            key={idx}
            style={[
              styles.dot,
              idx < currentIndex
                ? styles.dotDone
                : idx === currentIndex
                ? styles.dotCurrent
                : styles.dotUpcoming,
              idx === currentIndex && { width: 20 },
            ]}
          />
        ))}
      </View>

      {/* Category tile */}
      <View style={styles.categoryTile}>
        {iconRef.lib === 'mci' && (
          <MaterialCommunityIcons name={iconRef.name as any} size={32} color={Paprika.ai} />
        )}
        {iconRef.lib === 'ion' && (
          <Ionicons name={iconRef.name as any} size={32} color={Paprika.ai} />
        )}
        {iconRef.lib === 'feather' && (
          <Ionicons name={iconRef.name as any} size={32} color={Paprika.ai} />
        )}
      </View>

      {/* Question */}
      <Text style={styles.question}>{question}</Text>

      {/* Item name */}
      <Text style={styles.itemName}>{pantryName(item)}</Text>

      {/* Note */}
      <Text style={styles.note}>{note}</Text>

      {/* Buttons */}
      <View style={styles.buttonsContainer}>
        <Pressable
          style={({ pressed }) => [
            styles.keepButton,
            { backgroundColor: PaprikaFreshness.fresh.soft, opacity: pressed ? 0.8 : 1 },
          ]}
          onPress={handleKeep}
        >
          <Text style={[styles.keepButtonText, { color: PaprikaFreshness.fresh.text }]}>
            {keepButtonLabel}
          </Text>
        </Pressable>
        <Pressable
          style={({ pressed }) => [
            styles.tossButton,
            { opacity: pressed ? 0.6 : 1 },
          ]}
          onPress={handleToss}
        >
          <Text style={styles.tossButtonText}>{t('pantry.review.doneTossed')}</Text>
        </Pressable>
      </View>

      {/* Progress text */}
      <Text style={styles.progress}>
        {t('pantry.review.panelSub', { current: currentNum, total: totalItems })}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dotsContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 24,
    justifyContent: 'center',
  },
  dot: {
    height: 8,
    width: 8,
    borderRadius: 4,
  },
  dotDone: {
    backgroundColor: Paprika.ai,
  },
  dotCurrent: {
    backgroundColor: Paprika.ai,
  },
  dotUpcoming: {
    backgroundColor: Paprika.aiDots,
  },
  categoryTile: {
    width: 70,
    height: 70,
    borderRadius: 12,
    backgroundColor: Paprika.surfaceLowest,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
    borderColor: Paprika.surfaceHighest,
    borderWidth: 1,
  },
  question: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    fontWeight: '600',
    color: Paprika.ai,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  itemName: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 22,
    fontStyle: 'italic',
    color: Paprika.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  note: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 18,
  },
  buttonsContainer: {
    width: '100%',
    gap: 12,
    marginBottom: 16,
  },
  keepButton: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  keepButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    fontWeight: '600',
  },
  tossButton: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Paprika.textMuted,
  },
  tossButtonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    fontWeight: '500',
    color: Paprika.textMuted,
  },
  progress: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
});
